import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** A calendar date with no time component, e.g. "2026-09-17". */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Format a date for display, e.g. "Sep 17, 2026".
 *
 * Date-only strings ("YYYY-MM-DD", the shape SQL DATE columns should be
 * serialized as) are rendered as that calendar day regardless of the viewer's
 * timezone. `new Date("2026-09-17")` is midnight UTC, and formatting that in
 * US Eastern would print Sep 16, so those are pinned to UTC. Full timestamps
 * are real instants and keep rendering in the viewer's local timezone.
 */
export function formatDate(dateString: string | null): string {
  if (!dateString) return "--";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(DATE_ONLY_RE.test(dateString) ? { timeZone: "UTC" } : {}),
  });
}

/**
 * Format a DATE-ONLY string ("2026-08-19") without shifting it a day.
 *
 * `new Date("2026-08-19")` parses as UTC midnight, which is the evening of the
 * 18th anywhere west of Greenwich — so formatDate renders date-only values one
 * day early for US viewers. Splitting the parts sidesteps the timezone entirely.
 * Use this for calendar dates (report dates, "last sent"); use formatDate for
 * real timestamps.
 */
export function formatDateOnly(dateString: string | null): string {
  if (!dateString) return "--";
  const [y, m, d] = dateString.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return formatDate(dateString);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a timestamp as a facility-local (America/New_York) date + time, e.g.
 * "Jul 9, 2026, 2:15 PM". Renders in the plant timezone regardless of the
 * viewer's browser so the clock time lines up with the shift.
 */
export function formatDateTime(dateString: string | null): string {
  if (!dateString) return "--";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "--";
  return date.toLocaleString("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function timeAgo(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
}

export function statusColor(status: string): string {
  switch (status) {
    case "pending":
      return "text-[var(--warn-text-strong)] bg-[var(--warn-bg)]";
    case "in_progress":
      return "text-teal-700 dark:text-teal-400 bg-teal-400/10";
    case "completed":
      return "text-[var(--ok-text-strong)] bg-[var(--ok-bg)]";
    case "cancelled":
      return "text-gray-400 bg-gray-400/10";
    default:
      return "text-gray-400 bg-gray-400/10";
  }
}

export function priorityColor(priority: string): string {
  switch (priority) {
    case "urgent":
      return "text-[var(--bad-text-strong)] bg-[var(--bad-bg)]";
    case "high":
      return "text-orange-700 dark:text-orange-400 bg-orange-400/10";
    case "medium":
      return "text-[var(--warn-text-strong)] bg-[var(--warn-bg)]";
    case "low":
      return "text-gray-400 bg-gray-400/10";
    default:
      return "text-gray-400 bg-gray-400/10";
  }
}

export function formatHours(hours: number): string {
  // C4 fix: round to total minutes first to avoid "Nh 60m" from float precision
  const totalMinutes = Math.round(Math.abs(hours) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const sign = hours < 0 ? "-" : "";
  return m > 0 ? `${sign}${h}h ${m}m` : `${sign}${h}h`;
}

/** Whole hours with commas, no minutes (e.g. "1,234h") */
export function formatWholeHours(hours: number): string {
  const sign = hours < 0 ? "-" : "";
  const h = Math.round(Math.abs(hours));
  return `${sign}${new Intl.NumberFormat("en-US").format(h)}h`;
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat("en-US").format(n);
}

/** Signed percentage like "+5.3%" / "-2.0%", or "—" when not a finite number. */
export function formatSignedPercent(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${n >= 0 ? "+" : ""}${n.toFixed(digits)}%`;
}

/** Compact currency: $4.3M, $125K, $950 */
export function formatCurrencyCompact(amount: number): string {
  if (Math.abs(amount) >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(amount) >= 10_000) {
    return `$${(amount / 1_000).toFixed(0)}K`;
  }
  if (Math.abs(amount) >= 1_000) {
    return `$${(amount / 1_000).toFixed(1)}K`;
  }
  return formatCurrency(amount);
}

// H5 fix: use local time consistently instead of mixing local getDay() with UTC toISOString()
function toLocalDateStr(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function getPreviousBusinessDay(): string {
  const d = new Date();
  const day = d.getDay();
  if (day === 0) d.setDate(d.getDate() - 2);
  else if (day === 1) d.setDate(d.getDate() - 3);
  else d.setDate(d.getDate() - 1);
  return toLocalDateStr(d);
}

export function getToday(): string {
  return toLocalDateStr(new Date());
}

/**
 * Today's date (YYYY-MM-DD) in the facility timezone (America/New_York),
 * independent of the viewer's browser timezone. Use this when a date must line
 * up with the database's `CURDATE()` — e.g. deciding whether a supervisor
 * selection is "today / live" — so a non-ET browser (or a viewer near midnight)
 * doesn't seed a date a day off from the server's notion of today.
 */
export function getFacilityToday(): string {
  // en-CA formats as YYYY-MM-DD, which is exactly the shape we want.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

export function getMonthStart(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

/**
 * Given a "YYYY-MM" string, returns the last day of that month as "YYYY-MM-DD".
 * If the input refers to the current month, returns today's date (clamped MTD).
 */
export function getMonthEnd(yearMonth: string): string {
  const [yearStr, monthStr] = yearMonth.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr); // 1-12
  const today = new Date();
  if (year === today.getFullYear() && month === today.getMonth() + 1) {
    return getToday();
  }
  // new Date(year, month, 0) = last day of month `month` (month is 1-indexed here)
  const lastDay = new Date(year, month, 0);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${lastDay.getFullYear()}-${pad(lastDay.getMonth() + 1)}-${pad(lastDay.getDate())}`;
}

export function getYearStart(): string {
  return `${new Date().getFullYear()}-01-01`;
}

export type DateMode = "yesterday" | "today" | "mtd" | "ytd" | "month" | "range";
